<script setup>
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: an outgoing in-chat flow
// message (content_attributes.kaption_whatsapp_flow): header, body, footer and
// the CTA the customer taps, like WhatsApp shows it. Not flag-gated so flow
// history stays readable if the flag is turned off.
// whatsapp-flows.INBOUND.3 - "Waiting for answer" until the backend writes
// answered_at / response_message_id on this message (when the customer
// completes the flow), then "Answered" linking to the response bubble.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_STATUS } from '../constants';

const { content, contentAttributes, status } = useMessageContext();
const { t } = useI18n();

const flow = computed(() => contentAttributes.value?.kaptionWhatsappFlow || {});
const body = computed(() => flow.value.body || content.value);
const isAnswered = computed(
  () => !!(flow.value.answeredAt || flow.value.responseMessageId)
);
const isFailed = computed(() => status?.value === MESSAGE_STATUS.FAILED);

const openResponse = () => {
  if (!flow.value.responseMessageId) return;
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: flow.value.responseMessageId,
  });
};
</script>

<template>
  <BaseBubble
    class="px-4 py-3 min-w-64 max-w-sm"
    data-bubble-name="kaption-whatsapp-flow"
  >
    <div class="flex items-center gap-1.5 mb-1 text-xs text-n-slate-11">
      <span class="i-lucide-clipboard-list size-3.5" />
      {{ t('KAPTION_WHATSAPP_FLOWS.BUBBLE.SENT_FLOW') }}
      <template v-if="flow.flowName">{{ `· ${flow.flowName}` }}</template>
    </div>
    <div v-if="flow.header" class="font-semibold">{{ flow.header }}</div>
    <div v-if="body" class="whitespace-pre-wrap break-words">{{ body }}</div>
    <div v-if="flow.footer" class="mt-1 text-xs text-n-slate-11">
      {{ flow.footer }}
    </div>
    <div
      v-if="flow.cta"
      class="flex items-center justify-center gap-1.5 pt-2 mt-2 text-sm font-medium border-t border-n-weak text-n-blue-11"
      data-testid="kaption-flow-cta"
    >
      <span class="i-lucide-list-checks size-4" />
      {{ flow.cta }}
    </div>
    <button
      v-if="isAnswered"
      type="button"
      class="flex items-center gap-1 mt-2 text-xs font-medium text-n-teal-11 hover:underline"
      data-testid="kaption-flow-answered"
      @click="openResponse"
    >
      <span class="i-lucide-circle-check size-3.5" />
      {{ t('KAPTION_WHATSAPP_FLOWS.BUBBLE.ANSWERED') }}
    </button>
    <div
      v-else-if="!isFailed"
      class="flex items-center gap-1 mt-2 text-xs text-n-slate-11"
      data-testid="kaption-flow-waiting"
    >
      <span class="i-lucide-clock size-3.5" />
      {{ t('KAPTION_WHATSAPP_FLOWS.BUBBLE.WAITING_ANSWER') }}
    </div>
  </BaseBubble>
</template>
